import * as React from 'react'
import { MoreHorizontal, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/menu'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/** Fields every row action has, whatever kind it is. */
interface RowActionBase {
  /** What the menu item says, and the icon button's accessible name. */
  label: string
  icon: LucideIcon
  onSelect: () => void
  disabled?: boolean | undefined
  /**
   * Why it is disabled — shown under the label in the menu.
   *
   * Not a tooltip: a disabled control fires no pointer events, so the one row
   * that needs the explanation is the one that cannot show it.
   */
  disabledReason?: string | undefined
}

/**
 * A row action.
 *
 * The union is the contract: `destructive` and `priority` cannot both be set.
 * A destructive action is never one of the icons a row reveals on hover — it
 * belongs in the menu, where it is named and one keystroke further away. That
 * rule was prose once and the Users list still ended up showing nine red
 * "Delete" buttons at rest, so it is a type now.
 */
export type RowAction = RowActionBase &
  (
    | {
        /** Show as an icon beside the menu: the action this row is usually here for. */
        priority?: boolean | undefined
        destructive?: false | undefined
      }
    | {
        destructive: true
        priority?: never
      }
  )

interface Props {
  /**
   * What the row is about — a person's name, a token's name. Used for the
   * menu's accessible name, so "More actions" in a list of forty rows says
   * which row it belongs to.
   */
  label: string
  actions: ReadonlyArray<RowAction>
  className?: string | undefined
}

/** Reveal on approach, and only where approaching is possible — see the notes below. */
const ON_APPROACH = [
  // Underscores are spaces in an arbitrary variant. Written without them this
  // compiles to `@media(hover:hover)and(pointer:fine)`, which is invalid CSS and
  // is dropped in silence — the icons then sit on every row and it looks as
  // though the change was never applied. `cse/valid-arbitrary-variant` guards it.
  '[@media(hover:hover)_and_(pointer:fine)]:opacity-0',
  '[@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100',
  '[@media(hover:hover)_and_(pointer:fine)]:group-focus-within:opacity-100',
]

/**
 * The actions for one table row: a permanent `…` menu, with the common ones
 * surfacing as icons on approach.
 *
 * Two things went wrong with hiding a row's actions entirely until hover.
 * Nothing said the row had actions at all — a column labeled "Actions" over
 * three rows of nothing reads as broken, not as quiet. And every action had to
 * fit on the row, so a row with four of them became a strip of buttons
 * competing with the data it belonged to.
 *
 * So the menu is always there: one mark per row that says there is more here,
 * holding **every** action with its icon and its name. That is the only place
 * they are all named, which is what makes the icons beside it safe to hide —
 * nothing is reachable by hovering alone.
 *
 * On a touch device the icons stay visible: there is no pointer to approach
 * with, and tapping a row does not put focus inside it.
 *
 * Mirrors `.ds-row__actions` in the design system, and
 * `web/src/components/ui/row-actions.parity.spec.ts` compares the two.
 */
export function RowActions({ label, actions, className }: Props): React.ReactElement | null {
  if (actions.length === 0) return null

  const priority = actions.filter((action) => action.priority)
  const ordinary = actions.filter((action) => !action.destructive)
  const destructive = actions.filter((action) => action.destructive)

  return (
    <div className={cn('flex items-center justify-end gap-1', className)}>
      {priority.length > 0 ? (
        <div
          className={cn('flex items-center gap-1 transition-opacity motion-reduce:transition-none', ...ON_APPROACH)}
        >
          {priority.map((action) => (
            <PriorityAction key={action.label} action={action} />
          ))}
        </div>
      ) : null}

      <Menu>
        <MenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-[30px] w-[30px] p-0" aria-label={`Actions for ${label}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </MenuTrigger>
        <MenuContent>
          {ordinary.map((action) => (
            <ActionItem key={action.label} action={action} />
          ))}
          {ordinary.length > 0 && destructive.length > 0 ? <MenuSeparator /> : null}
          {destructive.map((action) => (
            <ActionItem key={action.label} action={action} />
          ))}
        </MenuContent>
      </Menu>
    </div>
  )
}

/** One item in the menu: icon, name, and the reason it cannot be used if it cannot. */
function ActionItem({ action }: { action: RowAction }): React.ReactElement {
  const Icon = action.icon
  return (
    <MenuItem
      destructive={action.destructive}
      disabled={action.disabled ?? false}
      onSelect={() => action.onSelect()}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        {action.label}
        {action.disabled && action.disabledReason ? (
          <span className="block text-xs text-ink-muted">{action.disabledReason}</span>
        ) : null}
      </span>
    </MenuItem>
  )
}

/**
 * A priority action as an icon.
 *
 * Icon-only, because the label is in the menu a few pixels away — repeating it
 * on the row is what made these strips of buttons in the first place. The
 * tooltip carries the name, and `aria-label` gives it to anything that does not
 * hover.
 */
function PriorityAction({ action }: { action: RowAction }): React.ReactElement {
  const Icon = action.icon
  const hint = action.disabled && action.disabledReason ? action.disabledReason : action.label
  return (
    <Tooltip content={hint}>
      {/* A span, because a disabled button fires no pointer events of its own,
          and the disabled one is exactly the icon whose hint matters. */}
      <span className="inline-flex">
        <Button
          variant="ghost"
          size="sm"
          className="h-[30px] w-[30px] p-0"
          aria-label={action.label}
          disabled={action.disabled}
          onClick={() => action.onSelect()}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </Button>
      </span>
    </Tooltip>
  )
}
